/**
 * Race Manager Page
 * Create, edit, and select races for timing
 */

import {
  getRaces,
  getSelectedRace,
  setSelectedRace,
  getAllAgeGroupPresets,
  loadRoster,
  hasRoster,
  clearAllData,
  resetRaceResults
} from '../lib/storage.js'
import { clearAllFirebaseData, saveRaceToCloud, deleteRaceFromCloud } from '../lib/firebase.js'
import { seedTestData } from '../lib/seed-data.js'
import { showConfirm } from '../lib/ui.js'

/**
 * Get pre-race checklist status
 * @param {Object} race - Race object
 * @returns {Object} Checklist with items and overall status
 */
function getRaceChecklist(race) {
  if (!race) return null

  const roster = loadRoster(race.id)
  const hasRosterData = roster && roster.length > 0

  // Check for duplicate bibs
  const bibCounts = {}
  roster.forEach(a => {
    bibCounts[a.bib] = (bibCounts[a.bib] || 0) + 1
  })
  const duplicateBibs = Object.entries(bibCounts)
    .filter(([, count]) => count > 1)
    .map(([bib]) => bib)

  // Check for athletes missing names
  const missingNames = roster.filter(a => !a.firstName && !a.lastName).length

  // Check for athletes missing gender
  const missingGender = roster.filter(a => !a.gender).length

  const items = [
    {
      id: 'race_name',
      label: 'Race name configured',
      status: race.name ? 'pass' : 'fail',
      detail: race.name || 'Missing race name'
    },
    {
      id: 'race_date',
      label: 'Race date set',
      status: race.date ? 'pass' : 'fail',
      detail: race.date ? formatDate(race.date) : 'No date set'
    },
    {
      id: 'roster_loaded',
      label: 'Roster loaded',
      status: hasRosterData ? 'pass' : 'warn',
      detail: hasRosterData ? `${roster.length} athletes` : 'No roster loaded'
    },
    {
      id: 'no_duplicates',
      label: 'No duplicate bibs',
      status: duplicateBibs.length === 0 ? 'pass' : 'fail',
      detail: duplicateBibs.length === 0 ? 'All bibs unique' : `Duplicates: ${duplicateBibs.join(', ')}`
    },
    {
      id: 'names_complete',
      label: 'All athletes have names',
      status: missingNames === 0 ? 'pass' : 'warn',
      detail: missingNames === 0 ? 'All names present' : `${missingNames} missing names`
    },
    {
      id: 'gender_complete',
      label: 'Gender assigned (for scoring)',
      status: missingGender === 0 ? 'pass' : 'warn',
      detail: missingGender === 0 ? 'All genders assigned' : `${missingGender} missing gender`
    }
  ]

  const passCount = items.filter(i => i.status === 'pass').length
  const failCount = items.filter(i => i.status === 'fail').length
  const warnCount = items.filter(i => i.status === 'warn').length

  let overallStatus = 'ready'
  if (failCount > 0) overallStatus = 'not_ready'
  else if (warnCount > 0) overallStatus = 'warnings'

  return {
    items,
    passCount,
    failCount,
    warnCount,
    overallStatus,
    rosterCount: roster.length
  }
}

/**
 * Render the pre-race checklist UI
 */
function renderRaceChecklist(race, onNavigate) {
  const checklist = getRaceChecklist(race)
  if (!checklist) return ''

  const statusIcons = {
    pass: '✓',
    fail: '✗',
    warn: '!'
  }

  const overallLabels = {
    ready: { text: 'Ready to Start', class: 'ready' },
    warnings: { text: 'Ready with Warnings', class: 'warnings' },
    not_ready: { text: 'Not Ready', class: 'not-ready' }
  }

  const overall = overallLabels[checklist.overallStatus]

  return `
    <div class="race-checklist">
      <div class="checklist-header">
        <h3>Pre-Race Checklist</h3>
        <span class="checklist-status ${overall.class}">${overall.text}</span>
      </div>

      <div class="checklist-items">
        ${checklist.items.map(item => `
          <div class="checklist-item ${item.status}">
            <span class="check-icon ${item.status}">${statusIcons[item.status]}</span>
            <span class="check-label">${item.label}</span>
            <span class="check-detail">${item.detail}</span>
          </div>
        `).join('')}
      </div>

      ${!hasRoster(race.id) ? `
        <div class="checklist-action">
          <button class="btn-upload-roster" id="uploadRosterBtn">Upload Roster</button>
        </div>
      ` : ''}

      <div class="current-race-banner">
        <span class="banner-label">Current Race:</span>
        <span class="banner-name">${race.name}</span>
        <button class="btn-go-tapper" id="goTapperBtn">Go to Tapper</button>
      </div>
    </div>
  `
}

// UI State
let showingForm = false
let editingRace = null

/**
 * Format date for display
 */
function formatDate(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

/**
 * Get status badge class and label
 */
function getStatusBadge(status) {
  const badges = {
    draft: { class: 'status-draft', label: 'Draft' },
    ready: { class: 'status-ready', label: 'Ready' },
    live: { class: 'status-live', label: 'Live' },
    finished: { class: 'status-finished', label: 'Finished' }
  }
  return badges[status] || badges.draft
}

/**
 * Get distance display string
 */
function formatDistance(race) {
  if (!race.distanceValue) return race.distance || ''
  const unit = race.distanceUnit === 'kilometers' ? 'km' : 'mi'
  return `${race.distanceValue} ${unit}`
}

/**
 * Render the Race Manager page
 */
export function renderRaceManagerPage(container, onNavigate) {
  const races = getRaces()
  const selectedRace = getSelectedRace()
  const presets = getAllAgeGroupPresets()

  // Sort races: live first, then by date descending
  races.sort((a, b) => {
    if (a.status === 'live' && b.status !== 'live') return -1
    if (b.status === 'live' && a.status !== 'live') return 1
    return new Date(b.date || 0) - new Date(a.date || 0)
  })

  container.innerHTML = `
    <div class="race-manager-page">
      <header class="rm-header">
        <h1>Race Manager</h1>
        ${!showingForm ? `
          <button class="btn-create" id="createRaceBtn">
            + New Race
          </button>
        ` : ''}
      </header>

      ${showingForm ? renderRaceForm(editingRace, presets) : ''}

      ${!showingForm ? `
        <div class="races-list">
          ${races.length === 0 ? `
            <div class="no-races">
              <p>No races configured yet.</p>
              <p class="text-secondary">Create a race to get started timing.</p>
              <div class="test-data-section">
                <button class="btn-test-data" id="loadTestDataBtn">Load Test Data (100 runners)</button>
                <p class="text-secondary">For volunteer training</p>
              </div>
            </div>
          ` : races.map(race => {
    const badge = getStatusBadge(race.status)
    const isSelected = selectedRace && selectedRace.id === race.id
    return `
              <div class="race-card ${isSelected ? 'selected' : ''}" data-race-id="${race.id}">
                <div class="race-info">
                  <div class="race-name">${race.name || 'Unnamed Race'}</div>
                  <div class="race-details">
                    ${formatDate(race.date)}
                    ${race.distanceValue ? ` • ${formatDistance(race)}` : ''}
                  </div>
                </div>
                <div class="race-status">
                  <span class="status-badge ${badge.class}">${badge.label}</span>
                </div>
                <div class="race-actions">
                  ${isSelected ? `
                    <span class="selected-badge">Selected</span>
                    <button class="btn-reset" data-action="reset" title="Reset Results (False Start)">↺ Reset</button>
                  ` : `
                    <button class="btn-select" data-action="select">Select</button>
                  `}
                  <button class="btn-edit" data-action="edit">Edit</button>
                </div>
              </div>
            `
  }).join('')}
        </div>

        ${selectedRace ? renderRaceChecklist(selectedRace, onNavigate) : ''}

        <div class="admin-section">
          <h3>Admin Tools</h3>
          <div class="admin-actions">
            <button class="btn-sync" id="syncFromCloudBtn">Reset & Sync from Cloud</button>
            <p class="text-secondary">Clears local data and downloads fresh from Firebase (use on new devices)</p>
          </div>
          <div class="admin-actions" style="margin-top: var(--spacing-md);">
            <button class="btn-danger" id="clearAllDataBtn">Clear All Data (Local + Firebase)</button>
            <p class="text-secondary">Deletes everything from local storage AND Firebase</p>
          </div>
        </div>
      ` : ''}
    </div>
  `

  attachRaceManagerHandlers(container, onNavigate)
}

/**
 * Render race creation/edit form
 */
function renderRaceForm(race, presets) {
  const isEdit = race && race.id
  const r = race || {
    name: '',
    date: new Date().toISOString().split('T')[0],
    distanceValue: '',
    distanceUnit: 'kilometers',
    ageGroupPreset: 'standard',
    raceType: 'road',
    status: 'draft'
  }

  return `
    <div class="race-form">
      <h2>${isEdit ? 'Edit Race' : 'Create New Race'}</h2>

      <div class="form-group">
        <label for="raceName">Race Name *</label>
        <input type="text" id="raceName" value="${r.name}" placeholder="e.g., Fall 5K Classic" required>
      </div>

      <div class="form-group">
        <label for="raceDate">Date *</label>
        <input type="date" id="raceDate" value="${r.date || ''}" required>
      </div>
      
      <div class="form-group">
        <label for="raceType">Race Type</label>
        <select id="raceType">
          <option value="road" ${r.raceType === 'road' ? 'selected' : ''}>Road / Community Race</option>
          <option value="xc" ${r.raceType === 'xc' ? 'selected' : ''}>Cross Country (Team Scoring)</option>
        </select>
      </div>

      <div class="form-row">
        <div class="form-group flex-1">
          <label for="distanceValue">Distance</label>
          <input type="number" id="distanceValue" value="${r.distanceValue || ''}" step="0.1" min="0" placeholder="5">
        </div>
        <div class="form-group flex-1">
          <label for="distanceUnit">Unit</label>
          <select id="distanceUnit">
            <option value="kilometers" ${r.distanceUnit === 'kilometers' ? 'selected' : ''}>Kilometers</option>
            <option value="miles" ${r.distanceUnit === 'miles' ? 'selected' : ''}>Miles</option>
          </select>
        </div>
      </div>

      <div class="form-group">
        <label>Age Groups</label>
        <div class="preset-options">
          ${Object.entries(presets).map(([key, preset]) => `
            <label class="preset-option ${r.ageGroupPreset === key ? 'selected' : ''}">
              <input type="radio" name="ageGroupPreset" value="${key}" ${r.ageGroupPreset === key ? 'checked' : ''}>
              <span class="preset-name">${preset.name}</span>
              ${key !== 'custom' && preset.groups.length > 0 ? `
                <span class="preset-preview">${preset.groups.map(g => g.name).slice(0, 3).join(', ')}${preset.groups.length > 3 ? '...' : ''}</span>
              ` : ''}
            </label>
          `).join('')}
        </div>
      </div>

      ${isEdit ? `
        <div class="form-group">
          <label for="raceStatus">Status</label>
          <select id="raceStatus">
            <option value="draft" ${r.status === 'draft' ? 'selected' : ''}>Draft</option>
            <option value="ready" ${r.status === 'ready' ? 'selected' : ''}>Ready</option>
            <option value="live" ${r.status === 'live' ? 'selected' : ''}>Live</option>
            <option value="finished" ${r.status === 'finished' ? 'selected' : ''}>Finished</option>
          </select>
        </div>
      ` : ''}

      <div class="form-actions">
        <button class="btn-cancel" id="cancelFormBtn">Cancel</button>
        <button class="btn-save" id="saveRaceBtn">${isEdit ? 'Save Changes' : 'Create Race'}</button>
      </div>

      ${isEdit ? `
        <div class="danger-zone">
          <button class="btn-delete" id="deleteRaceBtn">Delete Race</button>
        </div>
      ` : ''}
    </div>
  `
}

/**
 * Attach event handlers
 */
function attachRaceManagerHandlers(container, onNavigate) {
  // Create Race button
  document.getElementById('createRaceBtn')?.addEventListener('click', () => {
    showingForm = true
    editingRace = null
    renderRaceManagerPage(container, onNavigate)
  })

  // Cancel form button
  document.getElementById('cancelFormBtn')?.addEventListener('click', () => {
    showingForm = false
    editingRace = null
    renderRaceManagerPage(container, onNavigate)
  })

  // Save Race button
  document.getElementById('saveRaceBtn')?.addEventListener('click', async () => {
    const name = document.getElementById('raceName').value.trim()
    const date = document.getElementById('raceDate').value
    const distanceValue = parseFloat(document.getElementById('distanceValue').value) || null
    const distanceUnit = document.getElementById('distanceUnit').value
    const ageGroupPreset = document.querySelector('input[name="ageGroupPreset"]:checked')?.value || 'standard'
    const raceType = document.getElementById('raceType').value
    const status = document.getElementById('raceStatus')?.value || 'draft'

    if (!name) {
      alert('Race name is required')
      return
    }

    if (!date) {
      alert('Race date is required')
      return
    }

    const race = {
      ...(editingRace || {}),
      name,
      date,
      distanceValue,
      distanceUnit,
      ageGroupPreset,
      raceType,
      status
    }

    // Show saving state
    const btn = document.getElementById('saveRaceBtn')
    if (btn) {
      btn.disabled = true
      btn.textContent = 'Saving...'
    }

    // Save directly to Firebase
    const savedRace = await saveRaceToCloud(race)
    if (savedRace) {
      showingForm = false
      editingRace = null
      // Small delay to let subscription update
      setTimeout(() => renderRaceManagerPage(container, onNavigate), 500)
    } else {
      alert('Failed to save race. Check console for errors.')
      if (btn) {
        btn.disabled = false
        btn.textContent = editingRace ? 'Save Changes' : 'Create Race'
      }
    }
  })

  // Delete Race button
  document.getElementById('deleteRaceBtn')?.addEventListener('click', async () => {
    const confirmed = await showConfirm({
      title: 'Delete Race?',
      message: 'This will permanently delete the race and all its data. This cannot be undone.',
      confirmText: 'Delete Race',
      cancelText: 'Cancel',
      type: 'danger'
    })
    if (confirmed) {
      const btn = document.getElementById('deleteRaceBtn')
      if (btn) {
        btn.disabled = true
        btn.textContent = 'Deleting...'
      }

      // Delete directly from Firebase
      const success = await deleteRaceFromCloud(editingRace.id)
      if (success) {
        showingForm = false
        editingRace = null
        // Small delay to let subscription update
        setTimeout(() => renderRaceManagerPage(container, onNavigate), 500)
      } else {
        alert('Failed to delete race. Check console for errors.')
        if (btn) {
          btn.disabled = false
          btn.textContent = 'Delete Race'
        }
      }
    }
  })

  // Race card actions
  container.querySelectorAll('.race-card').forEach(card => {
    const raceId = card.dataset.raceId

    card.querySelector('[data-action="select"]')?.addEventListener('click', (e) => {
      e.stopPropagation()
      setSelectedRace(raceId)
      renderRaceManagerPage(container, onNavigate)
    })

    card.querySelector('[data-action="edit"]')?.addEventListener('click', (e) => {
      e.stopPropagation()
      const races = getRaces()
      editingRace = races.find(r => r.id === raceId)
      showingForm = true
      renderRaceManagerPage(container, onNavigate)
    })

    // Reset Result (False Start)
    card.querySelector('[data-action="reset"]')?.addEventListener('click', async (e) => {
      e.stopPropagation()
      const confirmed = await showConfirm({
        title: 'Reset Race Results?',
        message: 'This will clear all times and results for this race (e.g. for a false start), but KEEP the roster. This cannot be undone.',
        confirmText: 'Reset Results',
        cancelText: 'Cancel',
        type: 'warning'
      })
      if (confirmed) {
        resetRaceResults(raceId)
        alert('Race results have been reset.')
        renderRaceManagerPage(container, onNavigate)
      }
    })
  })

  // Preset option selection visual feedback
  container.querySelectorAll('.preset-option input').forEach(input => {
    input.addEventListener('change', () => {
      container.querySelectorAll('.preset-option').forEach(opt => {
        opt.classList.toggle('selected', opt.querySelector('input').checked)
      })

      // Auto-toggle Race Type if High School XC is selected
      if (input.value === 'high_school' && input.checked) {
        const typeSelect = document.getElementById('raceType')
        if (typeSelect) typeSelect.value = 'xc'
      }
    })
  })

  // Go to Tapper button
  document.getElementById('goTapperBtn')?.addEventListener('click', () => {
    onNavigate('tapper')
  })

  // Upload Roster button
  document.getElementById('uploadRosterBtn')?.addEventListener('click', () => {
    onNavigate('roster')
  })

  // Load Test Data button
  document.getElementById('loadTestDataBtn')?.addEventListener('click', async () => {
    const confirmed = await showConfirm({
      title: 'Load Test Data?',
      message: 'This will create "Superbowl 5k" race with 100 test runners (bibs 101-200).',
      confirmText: 'Load Test Data',
      cancelText: 'Cancel',
      type: 'warning'
    })
    if (confirmed) {
      seedTestData()
      renderRaceManagerPage(container, onNavigate)
    }
  })

  // Sync from Cloud button
  document.getElementById('syncFromCloudBtn')?.addEventListener('click', async () => {
    const confirmed = await showConfirm({
      title: 'Reset & Sync from Cloud?',
      message: 'This will clear all local data and download fresh from Firebase. Use this to set up a new device.',
      confirmText: 'Reset & Sync',
      cancelText: 'Cancel',
      type: 'warning'
    })
    if (confirmed) {
      const btn = document.getElementById('syncFromCloudBtn')
      if (btn) {
        btn.disabled = true
        btn.textContent = 'Syncing...'
      }
      // Call the global function
      if (window.resetAndSyncFromCloud) {
        await window.resetAndSyncFromCloud()
      }
    }
  })

  // Clear All Data button
  document.getElementById('clearAllDataBtn')?.addEventListener('click', async () => {
    const confirmed = await showConfirm({
      title: 'Clear ALL Data?',
      message: 'This will permanently delete ALL races, rosters, timestamps, and bib entries from BOTH local storage AND Firebase. This cannot be undone!',
      confirmText: 'Delete Everything',
      cancelText: 'Cancel',
      type: 'danger'
    })
    if (confirmed) {
      // Show loading state
      const btn = document.getElementById('clearAllDataBtn')
      if (btn) {
        btn.disabled = true
        btn.textContent = 'Deleting...'
      }

      try {
        // First, clear Firebase directly (not via queue)
        console.log('Clearing Firebase data...')
        const firebaseResult = await clearAllFirebaseData()
        console.log('Firebase clear result:', firebaseResult)

        // Then clear local storage
        console.log('Clearing local storage...')
        clearAllData()

        alert('All data deleted successfully!')
        window.location.reload()
      } catch (err) {
        console.error('Error clearing data:', err)
        alert('Error clearing data: ' + err.message)
        if (btn) {
          btn.disabled = false
          btn.textContent = 'Clear All Data (Local + Firebase)'
        }
      }
    }
  })
}
